import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Boxes, Pencil, Plus, Save, Trash2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { departmentMasterAPI } from '../services/api';
import { MASTER_ENTITY_CONFIG, flattenMasterHierarchy, getMasterCrudAccess } from '../lib/masterData';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { EmptyState, CardSkeleton } from '../components/common/States';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/utils';

const EMPTY_FORM = {
  name: '',
  location: '',
  departmentId: '',
  subDepartmentId: '',
  is_active: true,
};

const buildSuccessState = (message) => ({
  masterSuccessMessage: message,
  masterSuccessAt: Date.now(),
});

const AdminMasterEntityPage = ({ entityType, mode, entityId = null, routeBasePrefix = '/admin' }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const config = MASTER_ENTITY_CONFIG[entityType];
  const routeBase = `${routeBasePrefix}/master/${entityType}`;
  const access = useMemo(() => getMasterCrudAccess(user), [user]);
  const flattened = useMemo(() => flattenMasterHierarchy(rows), [rows]);
  const currentRecord = useMemo(() => {
    if (!entityId) return null;
    const collection =
      entityType === 'departments'
        ? flattened.departments
        : entityType === 'subdepartments'
          ? flattened.subDepartments
          : flattened.designations;
    return collection.find((row) => String(row.id) === String(entityId)) || null;
  }, [entityId, entityType, flattened]);
  const selectedDepartment = useMemo(
    () => flattened.departments.find((row) => String(row.id) === String(form.departmentId)),
    [flattened.departments, form.departmentId]
  );
  const subDepartmentOptions = useMemo(() => {
    if (!form.departmentId) return [];
    return flattened.subDepartments.filter((row) => String(row.department_id) === String(form.departmentId));
  }, [flattened.subDepartments, form.departmentId]);

  const loadRows = useCallback(async () => {
    try {
      setLoading(true);
      const response = await departmentMasterAPI.list({ include_inactive: true });
      setRows(response.data || []);
    } catch (error) {
      toast.error(getErrorMessage(error, `Failed to load ${config.pluralLabel.toLowerCase()}`));
    } finally {
      setLoading(false);
    }
  }, [config.pluralLabel]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  useEffect(() => {
    if (!currentRecord || mode === 'create') {
      setForm(EMPTY_FORM);
      return;
    }
    if (entityType === 'departments') {
      setForm({
        name: currentRecord.name || '',
        location: currentRecord.location || '',
        departmentId: '',
        subDepartmentId: '',
        is_active: Boolean(currentRecord.is_active),
      });
      return;
    }
    if (entityType === 'subdepartments') {
      setForm({
        name: currentRecord.name || '',
        location: '',
        departmentId: String(currentRecord.department_id || ''),
        subDepartmentId: '',
        is_active: Boolean(currentRecord.is_active),
      });
      return;
    }
    setForm({
      name: currentRecord.name || '',
      location: '',
      departmentId: String(currentRecord.department_id || ''),
      subDepartmentId: String(currentRecord.sub_department_id || ''),
      is_active: Boolean(currentRecord.is_active),
    });
  }, [currentRecord, entityType, mode]);

  const handleBack = () => {
    navigate(routeBase);
  };

  const handleCreateOrUpdate = async (event) => {
    event.preventDefault();
    try {
      setSaving(true);
      if (entityType === 'departments') {
        const payload = {
          name: form.name.trim(),
          location: form.location.trim() || null,
          ...(mode === 'edit' ? { is_active: Boolean(form.is_active) } : {}),
        };
        if (mode === 'create') {
          await departmentMasterAPI.createDepartment(payload);
          navigate(routeBase, { state: buildSuccessState('Department created successfully') });
          return;
        }
        await departmentMasterAPI.updateDepartment(Number(entityId), payload);
        navigate(routeBase, { state: buildSuccessState('Department updated successfully') });
        return;
      }

      if (entityType === 'subdepartments') {
        if (!form.departmentId) {
          toast.error('Department is required');
          return;
        }
        if (mode === 'create') {
          await departmentMasterAPI.createSubDepartment(Number(form.departmentId), { name: form.name.trim() });
          navigate(routeBase, { state: buildSuccessState('Sub-department created successfully') });
          return;
        }
        await departmentMasterAPI.updateSubDepartment(Number(entityId), {
          name: form.name.trim(),
          is_active: Boolean(form.is_active),
        });
        navigate(routeBase, { state: buildSuccessState('Sub-department updated successfully') });
        return;
      }

      const department = flattened.departments.find((row) => String(row.id) === String(form.departmentId));
      const subDepartment = flattened.subDepartments.find((row) => String(row.id) === String(form.subDepartmentId));
      if (!department || !form.subDepartmentId || !subDepartment) {
        toast.error('Department and sub-department are required');
        return;
      }
      if (mode === 'create') {
        await departmentMasterAPI.createDesignation({
          department_name: department.name,
          sub_department_name: subDepartment.name,
          name: form.name.trim(),
        });
        navigate(routeBase, { state: buildSuccessState('Designation created successfully') });
        return;
      }
      await departmentMasterAPI.updateDesignation(Number(entityId), {
        name: form.name.trim(),
        is_active: Boolean(form.is_active),
      });
      navigate(routeBase, { state: buildSuccessState('Designation updated successfully') });
    } catch (error) {
      const defaultMessage =
        mode === 'create'
          ? `Failed to create ${config.singularLabel.toLowerCase()}`
          : `Failed to update ${config.singularLabel.toLowerCase()}`;
      toast.error(getErrorMessage(error, defaultMessage));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      setSaving(true);
      if (entityType === 'departments') {
        await departmentMasterAPI.deleteDepartment(Number(entityId));
        navigate(routeBase, { state: buildSuccessState('Department deleted successfully') });
        return;
      }
      if (entityType === 'subdepartments') {
        await departmentMasterAPI.deleteSubDepartment(Number(entityId));
        navigate(routeBase, { state: buildSuccessState('Sub-department deleted successfully') });
        return;
      }
      await departmentMasterAPI.deleteDesignation(Number(entityId));
      navigate(routeBase, { state: buildSuccessState('Designation deleted successfully') });
    } catch (error) {
      toast.error(getErrorMessage(error, `Failed to delete ${config.singularLabel.toLowerCase()}`));
    } finally {
      setSaving(false);
    }
  };

  const canProceed =
    mode === 'view'
      ? access.canView
      : mode === 'create'
        ? access.canCreate
        : mode === 'edit'
          ? access.canEdit
          : access.canDelete;

  const pageTitle =
    mode === 'create'
      ? `Create ${config.singularLabel}`
      : mode === 'edit'
        ? `Edit ${config.singularLabel}`
        : mode === 'delete'
          ? `Delete ${config.singularLabel}`
          : `View ${config.singularLabel}`;

  if (!config) {
    return null;
  }

  if (!canProceed) {
    return (
      <Card>
        <CardContent className="py-4">
          <EmptyState
            icon={Boxes}
            title="Access Restricted"
            description="You do not have permission to open this page."
            actionLabel="Back"
            onAction={handleBack}
          />
        </CardContent>
      </Card>
    );
  }

  if ((mode === 'view' || mode === 'edit' || mode === 'delete') && !loading && !currentRecord) {
    return (
      <Card>
        <CardContent className="py-4">
          <EmptyState
            icon={Boxes}
            title={`${config.singularLabel} Not Found`}
            description="The selected record is not available."
            actionLabel="Back"
            onAction={handleBack}
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="grid h-11 w-11 place-items-center rounded-xl bg-primary/10 text-primary">
            <Boxes className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-2xl font-bold tracking-tight text-foreground">{pageTitle}</h2>
            <p className="text-sm text-muted-foreground">{config.pluralLabel} are managed on dedicated pages to keep the master workflow stable.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {mode === 'view' && access.canEdit ? (
            <Button type="button" variant="outline" onClick={() => navigate(`${routeBase}/${entityId}/edit`)}>
              <Pencil className="mr-2 h-4 w-4" />
              Edit
            </Button>
          ) : null}
          {mode === 'view' && access.canCreate ? (
            <Button type="button" variant="outline" onClick={() => navigate(`${routeBase}/new`)}>
              <Plus className="mr-2 h-4 w-4" />
              New
            </Button>
          ) : null}
          <Button type="button" variant="outline" onClick={handleBack}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{pageTitle}</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? <CardSkeleton rows={5} /> : null}

          {!loading && mode === 'view' && currentRecord ? (
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Name</p>
                <p className="font-medium">{currentRecord.name}</p>
              </div>
              {entityType === 'departments' ? (
                <div className="space-y-1">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Location</p>
                  <p>{currentRecord.location || '-'}</p>
                </div>
              ) : null}
              {entityType !== 'departments' ? (
                <div className="space-y-1">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Department</p>
                  <p>{currentRecord.department_name || '-'}</p>
                </div>
              ) : null}
              {entityType === 'designations' ? (
                <div className="space-y-1">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Sub-Department</p>
                  <p>{currentRecord.sub_department_name || '-'}</p>
                </div>
              ) : null}
              <div className="space-y-1">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Status</p>
                <Badge variant={currentRecord.is_active ? 'success' : 'secondary'}>
                  {currentRecord.is_active ? 'Active' : 'Inactive'}
                </Badge>
              </div>
              {access.canDelete ? (
                <div className="md:col-span-2">
                  <Button type="button" variant="destructive" onClick={() => navigate(`${routeBase}/${entityId}/delete`)}>
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </Button>
                </div>
              ) : null}
            </div>
          ) : null}

          {!loading && (mode === 'create' || mode === 'edit') ? (
            <form className="space-y-6" onSubmit={handleCreateOrUpdate}>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {entityType !== 'departments' ? (
                  <div className="space-y-2">
                    <Label>Department</Label>
                    <Select
                      value={form.departmentId || '_none'}
                      onValueChange={(value) =>
                        setForm((current) => ({
                          ...current,
                          departmentId: value === '_none' ? '' : value,
                          subDepartmentId: entityType === 'designations' ? '' : current.subDepartmentId,
                        }))
                      }
                      disabled={mode === 'edit'}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select department" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_none">None</SelectItem>
                        {flattened.departments.map((department) => (
                          <SelectItem key={department.id} value={String(department.id)}>
                            {department.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : null}

                {entityType === 'designations' ? (
                  <div className="space-y-2">
                    <Label>Sub-Department</Label>
                    <Select
                      value={form.subDepartmentId || '_none'}
                      onValueChange={(value) => setForm((current) => ({ ...current, subDepartmentId: value === '_none' ? '' : value }))}
                      disabled={mode === 'edit'}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select sub-department" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_none">None</SelectItem>
                        {subDepartmentOptions.map((subDepartment) => (
                          <SelectItem key={subDepartment.id} value={String(subDepartment.id)}>
                            {subDepartment.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : null}

                <div className="space-y-2">
                  <Label>{config.singularLabel} Name</Label>
                  <Input
                    value={form.name}
                    onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
                    placeholder={`Enter ${config.singularLabel.toLowerCase()} name`}
                  />
                </div>

                {entityType === 'departments' ? (
                  <div className="space-y-2">
                    <Label>Location</Label>
                    <Input
                      value={form.location}
                      onChange={(event) => setForm((current) => ({ ...current, location: event.target.value }))}
                      placeholder="Enter department location"
                    />
                  </div>
                ) : null}

                {mode === 'edit' ? (
                  <div className="flex items-center gap-2 sm:col-span-2 lg:col-span-3">
                    <Checkbox
                      id={`${entityType}-is-active`}
                      checked={Boolean(form.is_active)}
                      onCheckedChange={(checked) => setForm((current) => ({ ...current, is_active: Boolean(checked) }))}
                    />
                    <Label htmlFor={`${entityType}-is-active`}>Active</Label>
                  </div>
                ) : null}
              </div>

              {entityType === 'designations' && mode === 'create' && selectedDepartment ? (
                <p className="text-sm text-muted-foreground">
                  Creating under `{selectedDepartment.name}` with selected sub-department.
                </p>
              ) : null}

              <div className="flex items-center gap-2">
                <Button type="submit" disabled={saving}>
                  <Save className="mr-2 h-4 w-4" />
                  {saving ? 'Saving...' : mode === 'create' ? `Create ${config.singularLabel}` : `Save ${config.singularLabel}`}
                </Button>
                <Button type="button" variant="outline" onClick={handleBack}>
                  Cancel
                </Button>
              </div>
            </form>
          ) : null}

          {!loading && mode === 'delete' && currentRecord ? (
            <div className="space-y-6">
              <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-4">
                <p className="font-medium text-destructive">This action deletes the selected {config.singularLabel.toLowerCase()}.</p>
                <p className="mt-1 text-sm text-muted-foreground">Review the record below before continuing.</p>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-1">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Name</p>
                  <p className="font-medium">{currentRecord.name}</p>
                </div>
                {entityType !== 'departments' ? (
                  <div className="space-y-1">
                    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Department</p>
                    <p>{currentRecord.department_name || '-'}</p>
                  </div>
                ) : null}
                {entityType === 'designations' ? (
                  <div className="space-y-1">
                    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Sub-Department</p>
                    <p>{currentRecord.sub_department_name || '-'}</p>
                  </div>
                ) : null}
              </div>
              <div className="flex items-center gap-2">
                <Button type="button" variant="destructive" disabled={saving} onClick={handleDelete}>
                  <Trash2 className="mr-2 h-4 w-4" />
                  {saving ? 'Deleting...' : `Delete ${config.singularLabel}`}
                </Button>
                <Button type="button" variant="outline" onClick={handleBack}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminMasterEntityPage;
